import { useEffect, useRef, useState } from "react";
import { Music2, Play, Pause, Upload, Search, X, Loader2, Check } from "lucide-react";
import api from "../lib/api";

/**
 * MusicPicker — modal-style track picker for reels & live streaming.
 * Props:
 *  - open (bool), onClose
 *  - onSelect(track) — track: {id, title, artist, category, url, duration}
 *  - selected (id) — currently selected track id
 */
export default function MusicPicker({ open, onClose, onSelect, selected }) {
  const [tracks, setTracks] = useState({ royalty_free: [], user_tracks: [], categories: [] });
  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/music/library").then(r => setTracks(r.data || { royalty_free: [], user_tracks: [], categories: [] })).catch(() => {});
  }, [open]);

  useEffect(() => () => { audioRef.current?.pause?.(); }, []);

  const all = [...(tracks.user_tracks || []), ...(tracks.royalty_free || [])];
  const filtered = all.filter(t => {
    const matchCat = tab === "All" || t.category === tab;
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase()) || (t.artist || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const togglePlay = (track) => {
    if (audioRef.current && playingId === track.id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause?.();
    const a = new Audio(track.url);
    a.volume = 0.6;
    a.play().catch(() => {});
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    setPlayingId(track.id);
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("audio/")) { alert("Pick an audio file (mp3/m4a/wav)"); return; }
    if (f.size > 10 * 1024 * 1024) { alert("Audio must be under 10MB"); return; }
    setUploading(true);
    try {
      const { uploadToStorage } = await import("../lib/uploads");
      const cu = await uploadToStorage(f, "music");
      await api.post("/music/upload", {
        title: f.name.replace(/\.[^.]+$/, "").slice(0, 60),
        audio_url: cu.secure_url,
        duration: Math.round(cu.duration || 30),
      });
      // refresh
      const r = await api.get("/music/library");
      setTracks(r.data);
      setTab("My Uploads");
    } catch (e) {
      alert("Upload failed. Try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!open) return null;

  const allTabs = ["All", "My Uploads", ...(tracks.categories || [])];

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-end sm:items-center justify-center" onClick={onClose} data-testid="music-picker">
      <div onClick={e => e.stopPropagation()} className="w-full sm:max-w-md max-h-[85vh] bg-[#0B0F19] border-t sm:border border-white/15 sm:rounded-3xl rounded-t-3xl flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <Music2 className="w-5 h-5 text-[#A855F7]"/>
          <div className="flex-1 font-bold">Pick a track</div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="music-picker-close"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-3 flex items-center gap-2 border-b border-white/10">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10">
            <Search className="w-4 h-4 text-white/40"/>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tracks…"
              className="flex-1 bg-transparent outline-none text-sm" data-testid="music-search"/>
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="px-3 py-2 rounded-full bg-[#FF7A00] text-black font-bold text-xs flex items-center gap-1 active:scale-95 disabled:opacity-50"
            data-testid="music-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
            Upload
          </button>
          <input ref={fileRef} type="file" accept="audio/*" onChange={onUpload} className="hidden"/>
        </div>

        <div className="flex gap-1.5 overflow-x-auto px-3 py-2 border-b border-white/10 scrollbar-none">
          {allTabs.map(c => (
            <button key={c} onClick={() => setTab(c)}
              data-testid={`music-tab-${c.replace(/\s/g, '-').toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap active:scale-95 ${tab === c ? "bg-[#A855F7] text-white font-bold" : "bg-white/5 text-white/70 border border-white/10"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {filtered.length === 0 && (
            <div className="p-6 text-center text-white/50 text-sm">No tracks match your search.</div>
          )}
          {filtered.map(t => {
            const isPlaying = playingId === t.id;
            const isSelected = selected === t.id;
            return (
              <div key={t.id} data-testid={`music-track-${t.id}`}
                className={`flex items-center gap-3 p-2.5 rounded-2xl border ${isSelected ? "bg-[#A855F7]/15 border-[#A855F7]/50" : "bg-white/5 border-white/10"}`}>
                <button onClick={() => togglePlay(t)} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-90"
                  data-testid={`music-play-${t.id}`}>
                  {isPlaying ? <Pause className="w-4 h-4 text-[#A855F7]"/> : <Play className="w-4 h-4 text-white"/>}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{t.title}</div>
                  <div className="text-[10px] text-white/50 truncate">{t.artist} · {t.category} · {t.duration}s</div>
                </div>
                <button onClick={() => { onSelect(t); onClose(); }}
                  data-testid={`music-select-${t.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold active:scale-95 ${isSelected ? "bg-emerald-500 text-white" : "bg-[#A855F7] text-white"}`}>
                  {isSelected ? <Check className="w-3.5 h-3.5"/> : "Use"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
